/**
 * ADCOEMeasureDrilldown
 *
 * Two render paths based on measure category:
 * 1. FacilityDrilldown — outcome measures (AWV, A1C, BCS, HTN, PHQ9) and PIMs
 * 2. AuditDrilldown   — consistency/audit measures (Coding, Quality_Audits)
 *
 * Patterns ported from PIMsGrid: TanStack Table, imperative load-on-first-expand,
 * Framer Motion expand animation, SubGrid/SubGridContent, toggle search, ResizeObserver.
 */

import { Fragment, useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table'
import {
  AnimatedTableWrapper,
  AnimatedTableBody,
  AnimatedTableRow,
} from '@/components/tailwindadmin/components/animated-component/AnimatedTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  ChevronRight,
  Search,
  AlertCircle,
  ArrowUpDown,
  Users,
  CheckCircle2,
  ShieldAlert,
  Lightbulb,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'
import { useClientSort, useClientFilter } from '@/views/equip/leadership/hooks/useClientSort'
import { equipApi } from '@/services/equipApi'
import {
  useLeaderMeasureFacilities,
  useLeaderMeasurePatients,
  useLeaderAuditClinicians,
} from '@/hooks/equip/useEquipDashboard'
import { equipTable } from '@/views/equip/equip-table-styles'
import { SortableHeader } from '@/views/equip/components/SortableHeader'
import { SubGrid, SubGridContent } from '@/components/ui/sub-grid'
import { MEASURE_DEFINITIONS } from '@/lib/equip-measure-definitions'
import type {
  LeaderMeasureFacility,
  LeaderMeasurePatient,
  LeaderAuditClinician,
  AuditListItem,
  IncentiveMeasure,
} from '@/lib/equip-types'

/* ─── Props ─── */

interface ADCOEMeasureDrilldownProps {
  measureName: string
  measureCategory: string // 'Outcome' | 'Consistency'
  leaderEmail: string
  year: number
  quarter: number
  measure?: IncentiveMeasure
  onPatientClick?: (patientId: number) => void
  onAuditClick?: (auditId: number, auditType: string) => void
}

/* ─── Accent color palette (matches MeasureAccordionItem) ─── */

function getAccent(measure?: IncentiveMeasure) {
  const isZeroDenom = measure ? measure.denominator === 0 && measure.passed == null : false
  const isPassing = measure?.passed === true

  if (isZeroDenom) {
    return {
      ring: 'ring-slate-200/60 dark:ring-slate-700/60',
      text: 'text-blue-700 dark:text-blue-300',
      pctBg: 'bg-blue-100 dark:bg-blue-500/45',
      gradientVar: 'var(--equip-gradient-zero)',
    }
  }
  if (isPassing) {
    return {
      ring: 'ring-slate-200/60 dark:ring-slate-700/60',
      text: 'text-emerald-700 dark:text-emerald-300',
      pctBg: 'bg-emerald-100 dark:bg-teal-500/45',
      gradientVar: 'var(--equip-gradient-pass)',
    }
  }
  return {
    ring: 'ring-slate-200/60 dark:ring-slate-700/60',
    text: 'text-rose-700 dark:text-red-300',
    pctBg: 'bg-rose-100 dark:bg-red-500/45',
    gradientVar: 'var(--equip-gradient-fail)',
  }
}

/* ─── Drilldown status components ─── */

function DrilldownLoading({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="flex items-center gap-2 py-6 justify-center">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  )
}

function DrilldownError({ message = 'Failed to load data' }: { message?: string }) {
  return (
    <div className="flex items-center gap-2 py-6 justify-center text-destructive">
      <AlertCircle className="h-4 w-4" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

function DrilldownEmpty({ message = 'No data found' }: { message?: string }) {
  return <p className="text-sm text-muted-foreground py-6 text-center">{message}</p>
}

/* ─── Stat tile builder ─── */

function buildMeasureStatTiles(measure?: IncentiveMeasure) {
  if (!measure) return []
  return [
    { value: measure.denominator, label: 'Eligible', variant: 'slate' as const },
    { value: measure.numerator, label: 'Met', variant: 'emerald' as const },
    { value: Math.max(0, measure.denominator - measure.numerator), label: 'Still Needed', variant: 'amber' as const },
  ]
}

/* ─── Shared sub-components for hero stats & measure info ─── */

function StatTile({
  value,
  label,
  variant,
}: {
  value: number | string
  label: string
  variant: 'slate' | 'emerald' | 'amber'
}) {
  const bg = {
    slate: 'bg-slate-50 dark:bg-slate-400/25 border-slate-200 dark:border-slate-400/30',
    emerald: 'bg-emerald-50/70 dark:bg-teal-500/30 border-emerald-200 dark:border-teal-500/40',
    amber: 'bg-amber-50/70 dark:bg-amber-500/30 border-amber-200 dark:border-amber-500/40',
  }
  const text = {
    slate: 'text-foreground',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
  }
  return (
    <div className={cn('rounded-lg border px-3 py-2.5 flex flex-col items-center justify-center', bg[variant])}>
      <p className={cn('text-xl font-bold tabular-nums leading-none', text[variant])}>{value}</p>
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-1.5">
        {label}
      </p>
    </div>
  )
}

function InfoBlock({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: string
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 shrink-0">{icon}</div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-0.5">{title}</p>
          <p className="text-[13px] text-muted-foreground leading-relaxed">{children}</p>
        </div>
      </div>
    </div>
  )
}

function HeroStats({
  measure,
  accent,
  statTiles,
}: {
  measure: IncentiveMeasure
  accent: ReturnType<typeof getAccent>
  statTiles: { value: number | string; label: string; variant: 'slate' | 'emerald' | 'amber' }[]
}) {
  const isZeroDenom = measure.denominator === 0 && measure.passed == null
  const pct = measure.passRate != null ? Math.round(measure.passRate * 1000) / 10 : null
  // Display threshold comes from the backend payload (canonical config). Fall back
  // to 90% only if the backend hasn't been redeployed yet with the new column.
  const threshold = Math.round(measure.thresholdDisplay ?? 90)

  return (
    <div className="flex items-stretch gap-3">
      {/* Large percentage */}
      <div className={cn(
        'flex flex-col items-center justify-center rounded-xl px-5 py-3 min-w-[90px]',
        accent.pctBg,
      )}>
        <span className={cn('text-3xl font-extrabold tabular-nums leading-none', accent.text)}>
          {isZeroDenom ? 'N/A' : pct != null ? `${pct}%` : '--'}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-1">
          Rate
        </span>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 flex-1">
        {statTiles.map((tile) => (
          <StatTile key={tile.label} value={tile.value} label={tile.label} variant={tile.variant} />
        ))}
      </div>

      {/* Threshold badge */}
      <div className="flex flex-col items-end justify-center gap-1 pl-2">
        {isZeroDenom ? (
          <Badge variant="secondary" className="text-xs">
            Not Scored
          </Badge>
        ) : (
          <Badge variant={measure.passed ? 'lightSuccess' : 'lightError'} className="text-xs">
            {measure.passed ? 'Passing' : 'Below Threshold'}
          </Badge>
        )}
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {threshold}% target
        </span>
      </div>
    </div>
  )
}

function MeasureInfoPanel({ measureName }: { measureName: string }) {
  const def = MEASURE_DEFINITIONS[measureName]
  if (!def) return null

  return (
    <div className="rounded-lg border border-border bg-background divide-y divide-border">
      {/* Summary */}
      <div className="px-4 py-3">
        <h4 className="text-base font-semibold text-foreground mb-1">
          {def.displayName}
        </h4>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          {def.summary}
        </p>
      </div>

      {/* Eligibility + Met criteria */}
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
        <InfoBlock
          icon={<Users className="h-4 w-4 text-blue-500" />}
          title="Who's Eligible"
        >
          {def.eligibility}
        </InfoBlock>
        <InfoBlock
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
          title="Counts as Met"
        >
          {def.metWhen}
        </InfoBlock>
      </div>

      {/* Exclusions */}
      {def.exclusions && def.exclusions.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <div>
              <span className="text-xs font-semibold text-muted-foreground">Exclusions: </span>
              <span className="text-xs text-muted-foreground">
                {def.exclusions.join(' \u00B7 ')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      {def.tips && (
        <div className="px-4 py-3 bg-blue-50/40 dark:bg-blue-950/40">
          <div className="flex items-start gap-2">
            <Lightbulb className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">{def.tips}</p>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Main Router ─── */

export function ADCOEMeasureDrilldown(props: ADCOEMeasureDrilldownProps) {
  const isAudit =
    props.measureCategory === 'Consistency' &&
    (props.measureName === 'Coding' || props.measureName === 'Quality_Audits')
  if (isAudit) return <AuditDrilldown {...props} />
  if (props.measureName === 'PIMs') return <FacilityDrilldown {...props} />
  return <PatientDrilldown {...props} />
}

/* ═══════════════════════════════════════════════════════════════════════
   PATIENT DRILLDOWN — outcome measures + PIMs (flat patient list)
   ═══════════════════════════════════════════════════════════════════════ */

/* ─── Measure-specific patient columns ─── */

interface PatientColumnDef {
  header: string
  accessor: (p: LeaderMeasurePatient) => string
}

const MEASURE_PATIENT_COLUMNS: Record<string, PatientColumnDef[]> = {
  A1C: [
    { header: 'A1C Value', accessor: (p) => p.a1cValue ?? '—' },
    { header: 'A1C Date', accessor: (p) => p.a1cDate ?? '—' },
  ],
  AWV: [
    { header: 'AWV Month', accessor: (p) => p.awvMonth ?? '—' },
    { header: 'Status', accessor: (p) => p.awvStatus ?? '—' },
    { header: 'AWV Date', accessor: (p) => p.awvDate ?? '—' },
  ],
  BCS: [{ header: 'Status', accessor: (p) => p.bcsStatus ?? '—' }],
  CRCS: [
    { header: 'Last Screening', accessor: (p) => p.crcsLastScreeningDate ?? '—' },
    { header: 'Modality', accessor: (p) => p.crcsLastScreeningModality ?? '—' },
    { header: 'Status', accessor: (p) => p.crcsStatus ?? '—' },
  ],
  HTN: [
    { header: 'Last BP', accessor: (p) => p.lastBp ?? '—' },
    { header: 'BP Date', accessor: (p) => p.lastBpDate ?? '—' },
  ],
  PHQ9: [
    { header: 'PHQ-9 Score', accessor: (p) => p.phq9TotalScore?.toString() ?? '—' },
    { header: 'Last Date', accessor: (p) => p.lastPhq9Date ?? '—' },
  ],
  PIMs: [
    { header: 'PCP Score', accessor: (p) => p.pcpPimsScore?.toFixed(1) ?? '—' },
    { header: 'Psych Score', accessor: (p) => p.psychPimsScore?.toFixed(1) ?? '—' },
    { header: 'ACB', accessor: (p) => p.acbScore?.toFixed(1) ?? '—' },
    { header: 'Routine Meds', accessor: (p) => p.totalRoutineMeds?.toString() ?? '—' },
  ],
}

function PatientDrilldown({
  measureName,
  leaderEmail,
  year,
  quarter,
  measure,
  onPatientClick,
}: ADCOEMeasureDrilldownProps) {
  const accent = getAccent(measure)
  const [globalFilter, setGlobalFilter] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  // React Query: fetch all patients for this measure across all facilities
  const { data, isLoading, isError } = useLeaderMeasurePatients(
    leaderEmail,
    measureName,
    year,
    quarter
  )

  const patients = data?.patients ?? []

  const filtered = useClientFilter(patients, globalFilter, ['patientName', 'providerName', 'facilityName'] as (keyof LeaderMeasurePatient)[])
  const { sorted, sortKey, handleSort } = useClientSort(filtered, 'patientName', 'asc')

  if (isLoading) return <DrilldownLoading message="Loading patients..." />
  if (isError) return <DrilldownError message="Failed to load patient data" />

  const measureCols = MEASURE_PATIENT_COLUMNS[measureName] ?? []
  const statTiles = buildMeasureStatTiles(measure)

  const baseColumns: { key: string; label: string }[] = [
    { key: 'patientName', label: 'Patient' },
    { key: 'facilityName', label: 'Facility' },
    { key: 'providerName', label: 'Provider' },
    { key: 'isMet', label: 'Status' },
  ]

  const extraHeaders = measureCols.map((mc, i) => ({
    key: `extra_${i}`,
    label: mc.header,
  }))

  const allHeaders = [...baseColumns, ...extraHeaders]

  return (
    <div className="space-y-4">
      {/* Hero stats */}
      {measure && <HeroStats measure={measure} accent={accent} statTiles={statTiles} />}

      {/* Measure info panel */}
      <MeasureInfoPanel measureName={measureName} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {data ? `${data.totalCount} Patients` : 'Patients'}
        </h4>
        <div className="flex items-center gap-2">
          {!showSearch ? (
            <Button variant="ghost" size="icon" onClick={() => setShowSearch(true)}>
              <Search className="h-4 w-4" />
            </Button>
          ) : (
            <Input
              placeholder="Search patients..."
              className="w-56"
              autoFocus
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              onBlur={() => {
                if (!globalFilter) setShowSearch(false)
              }}
            />
          )}
        </div>
      </div>

      {/* Patient table */}
      {patients.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No patients found</p>
      ) : (
        <div className={cn(equipTable.wrapper, 'border rounded-lg border-border overflow-x-auto overscroll-x-contain bg-card')}>
          <table className={equipTable.table}>
            <thead className={equipTable.thead}>
              <tr className={equipTable.theadTr}>
                {allHeaders.map((col, colIdx) => (
                  <th
                    key={col.key}
                    className={cn(
                      equipTable.th,
                      'cursor-pointer select-none hover:text-primary/80',
                      col.key === 'patientName' && 'min-w-[160px]',
                      colIdx === 0 && equipTable.stickyTh
                    )}
                    onClick={() => {
                      if (colIdx < baseColumns.length) handleSort(col.key)
                    }}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {colIdx < baseColumns.length && (
                        <ArrowUpDown
                          className={cn(
                            'h-3 w-3',
                            sortKey === col.key ? 'opacity-100' : 'opacity-30'
                          )}
                        />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <AnimatedTableBody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={allHeaders.length} className="text-center py-4 text-muted-foreground">
                    No patients match your search
                  </td>
                </tr>
              ) : (
                sorted.map((patient, pIdx) => (
                  <AnimatedTableRow
                    key={patient.patientId}
                    index={pIdx}
                    className={cn(
                      onPatientClick ? equipTable.trClickable : equipTable.tr,
                      equipTable.trAlt(pIdx),
                      'group'
                    )}
                    onClick={() => onPatientClick?.(patient.patientId)}
                  >
                    <td
                      className={cn(
                        equipTable.td,
                        'font-medium',
                        pIdx % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd
                      )}
                    >
                      {patient.patientName}
                    </td>
                    <td className={equipTable.td}>{patient.facilityName ?? '—'}</td>
                    <td className={equipTable.td}>{patient.providerName ?? '—'}</td>
                    <td className={equipTable.td}>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                          patient.isMet === true
                            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                            : patient.isMet === false
                              ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                              : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {patient.isMet === true ? 'Met' : patient.isMet === false ? 'Not Met' : '—'}
                      </span>
                    </td>
                    {measureCols.map((mc, i) => (
                      <td key={i} className={equipTable.td}>
                        {mc.accessor(patient)}
                      </td>
                    ))}
                  </AnimatedTableRow>
                ))
              )}
            </AnimatedTableBody>
          </table>
        </div>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════
   FACILITY DRILLDOWN — PIMs (facility→patient hierarchy, mirrors clinician PIMsGrid)
   ═══════════════════════════════════════════════════════════════════════ */

interface FacilityWithPatients extends LeaderMeasureFacility {
  patients?: LeaderMeasurePatient[]
  patientsLoading?: boolean
  patientsTotalCount?: number
  patientsPage?: number
}

function FacilityDrilldown({
  measureName,
  leaderEmail,
  year,
  quarter,
  measure,
  onPatientClick,
}: ADCOEMeasureDrilldownProps) {
  const accent = getAccent(measure)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [expanded, setExpanded] = useState({})
  const [globalFilter, setGlobalFilter] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  const { data: facilityData = [], isLoading, isError } = useLeaderMeasureFacilities(
    leaderEmail,
    measureName,
    year,
    quarter
  )

  const [facilities, setFacilities] = useState<FacilityWithPatients[]>([])

  useEffect(() => {
    setFacilities(facilityData)
  }, [facilityData])

  const loadFacilityPatients = useCallback(
    async (facilityId: number, page = 1) => {
      setFacilities((prev) =>
        prev.map((f) =>
          f.facilityId === facilityId ? { ...f, patientsLoading: true } : f
        )
      )
      try {
        const response = await equipApi.dashboard.getLeaderMeasureFacilityPatients(
          leaderEmail, measureName, facilityId, year, quarter, page, 200
        )
        setFacilities((prev) =>
          prev.map((f) => {
            if (f.facilityId !== facilityId) return f
            const existing = page > 1 && f.patients ? f.patients : []
            return {
              ...f,
              patients: [...existing, ...response.patients],
              patientsLoading: false,
              patientsTotalCount: response.totalCount,
              patientsPage: page,
            }
          })
        )
      } catch (error) {
        console.error('Error loading facility patients:', error)
        setFacilities((prev) =>
          prev.map((f) =>
            f.facilityId === facilityId ? { ...f, patientsLoading: false } : f
          )
        )
      }
    },
    [leaderEmail, measureName, year, quarter]
  )

  const handleToggleExpanded = useCallback(
    (facilityId: number) => {
      const facility = facilities.find((f) => f.facilityId === facilityId)
      if (facility && !facility.patients && !facility.patientsLoading) {
        loadFacilityPatients(facilityId)
      }
    },
    [facilities, loadFacilityPatients]
  )

  const columnHelper = createColumnHelper<FacilityWithPatients>()
  const columns = useMemo(
    () => [
      columnHelper.accessor('facilityName', {
        header: ({ column }) => <SortableHeader label="Facility" column={column} />,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <ChevronRight
              className={cn(
                'h-4 w-4 shrink-0 transition-transform text-muted-foreground',
                row.getIsExpanded() && 'rotate-90'
              )}
            />
            <span className="text-sm font-medium">{row.original.facilityName}</span>
          </div>
        ),
      }),
      columnHelper.accessor('numerator', {
        header: ({ column }) => <SortableHeader label="Met" column={column} />,
        cell: ({ row }) => <p className="text-sm">{row.original.numerator}</p>,
      }),
      columnHelper.accessor('denominator', {
        header: ({ column }) => <SortableHeader label="Eligible" column={column} />,
        cell: ({ row }) => <p className="text-sm">{row.original.denominator}</p>,
      }),
      columnHelper.accessor('rate', {
        header: ({ column }) => <SortableHeader label="Rate" column={column} />,
        cell: ({ row }) => {
          const rate = row.original.rate
          return (
            <p className="text-sm font-medium">
              {rate != null ? `${rate.toFixed(1)}%` : '—'}
            </p>
          )
        },
      }),
    ],
    [columnHelper]
  )

  const table = useReactTable({
    data: facilities,
    columns,
    state: { sorting, globalFilter, expanded },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  })

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    cleanupRef.current?.()
    const sync = () => el.style.setProperty('--container-w', `${el.clientWidth}px`)
    sync()
    const ro = new ResizeObserver(() => sync())
    ro.observe(el)
    cleanupRef.current = () => ro.disconnect()
    return () => {
      ro.disconnect()
      cleanupRef.current = null
    }
  }, [isLoading])

  if (isLoading) return <DrilldownLoading message="Loading facility breakdown..." />
  if (isError) return <DrilldownError message="Failed to load facility breakdown" />
  if (facilities.length === 0) return <DrilldownEmpty message="No facilities found" />

  const measureCols = MEASURE_PATIENT_COLUMNS[measureName] ?? []
  const statTiles = buildMeasureStatTiles(measure)

  return (
    <div className="space-y-4">
      {measure && <HeroStats measure={measure} accent={accent} statTiles={statTiles} />}
      <MeasureInfoPanel measureName={measureName} />

      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Facility Breakdown
        </h4>
        <div className="flex items-center gap-2">
          {!showSearch ? (
            <Button variant="ghost" size="icon" onClick={() => setShowSearch(true)}>
              <Search className="h-4 w-4" />
            </Button>
          ) : (
            <Input
              placeholder="Search facilities..."
              className="w-56"
              autoFocus
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              onBlur={() => { if (!globalFilter) setShowSearch(false) }}
            />
          )}
        </div>
      </div>

      <div className={equipTable.wrapper}>
        <div ref={scrollContainerRef} className="overflow-auto">
          <AnimatedTableWrapper>
            <table className={equipTable.table}>
              <thead className={equipTable.thead}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={equipTable.theadTr}>
                    {headerGroup.headers.map((header, headerIdx) => (
                      <th
                        key={header.id}
                        className={cn(equipTable.th, headerIdx === 0 && equipTable.stickyTh)}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <AnimatedTableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-4 text-muted-foreground">
                      No facilities match your search
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, index) => (
                    <Fragment key={row.id}>
                      <AnimatedTableRow
                        index={index}
                        className={`${equipTable.tr} ${equipTable.trAlt(index)} cursor-pointer`}
                        onClick={() => {
                          row.toggleExpanded()
                          handleToggleExpanded(row.original.facilityId)
                        }}
                      >
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td
                            key={cell.id}
                            className={cn(
                              equipTable.td,
                              cellIdx === 0 &&
                                (index % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd)
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </AnimatedTableRow>
                      <AnimatePresence initial={false}>
                        {row.getIsExpanded() && (
                          <tr>
                            <td colSpan={columns.length} className="p-0">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  height: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
                                  opacity: { duration: 0.25, delay: 0.08 },
                                }}
                                style={{ clipPath: 'inset(0)' }}
                              >
                                <div
                                  className="sticky left-0 [overflow:clip]"
                                  style={{ width: 'var(--container-w)' }}
                                >
                                  <SubGrid isExpanded={true} depth={1} variant="compact">
                                    <SubGridContent>
                                      {row.original.patientsLoading ? (
                                        <div className="flex items-center gap-2 py-4 px-3">
                                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                          <p className="text-sm text-muted-foreground">Loading patients...</p>
                                        </div>
                                      ) : row.original.patients && row.original.patients.length > 0 ? (
                                        <>
                                          <FacilityPatientTable
                                            patients={row.original.patients}
                                            measureColumns={measureCols}
                                            globalFilter={globalFilter}
                                            onPatientClick={onPatientClick}
                                          />
                                          {row.original.patientsTotalCount != null &&
                                            row.original.patients.length < row.original.patientsTotalCount && (
                                              <div className="flex items-center justify-between px-3 py-2 border-t border-border/50">
                                                <p className="text-xs text-muted-foreground">
                                                  Showing {row.original.patients.length} of {row.original.patientsTotalCount} patients
                                                </p>
                                                <Button
                                                  variant="ghost" size="sm" className="text-xs h-7"
                                                  disabled={row.original.patientsLoading}
                                                  onClick={() => loadFacilityPatients(row.original.facilityId, (row.original.patientsPage ?? 1) + 1)}
                                                >
                                                  Load More
                                                </Button>
                                              </div>
                                            )}
                                        </>
                                      ) : row.original.patients ? (
                                        <p className="text-sm text-muted-foreground py-3 px-3">
                                          No patients found for this facility.
                                        </p>
                                      ) : (
                                        <div
                                          className="flex items-center gap-2 py-4 px-3 text-destructive/70 cursor-pointer hover:text-destructive"
                                          onClick={(e) => { e.stopPropagation(); loadFacilityPatients(row.original.facilityId) }}
                                        >
                                          <AlertCircle className="h-4 w-4 shrink-0" />
                                          <p className="text-sm">Failed to load patients. Click to retry.</p>
                                        </div>
                                      )}
                                    </SubGridContent>
                                  </SubGrid>
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </Fragment>
                  ))
                )}
              </AnimatedTableBody>
            </table>
          </AnimatedTableWrapper>
        </div>
      </div>
    </div>
  )
}

/* ─── Patient sub-table within a facility row ─── */

function FacilityPatientTable({
  patients,
  measureColumns,
  globalFilter,
  onPatientClick,
}: {
  patients: LeaderMeasurePatient[]
  measureColumns: PatientColumnDef[]
  globalFilter: string
  onPatientClick?: (patientId: number) => void
}) {
  const filtered = useClientFilter(patients, globalFilter, ['patientName', 'providerName'] as (keyof LeaderMeasurePatient)[])
  const { sorted, sortKey, handleSort } = useClientSort(filtered, 'patientName', 'asc')

  const baseColumns = [
    { key: 'patientName', label: 'Patient' },
    { key: 'isMet', label: 'Status' },
  ]
  const extraHeaders = measureColumns.map((mc, i) => ({ key: `extra_${i}`, label: mc.header }))
  const allHeaders = [...baseColumns, ...extraHeaders]

  return (
    <div className="border rounded-lg border-ld overflow-x-auto overscroll-x-contain bg-card">
      <table className={equipTable.table}>
        <thead className={equipTable.thead}>
          <tr className={equipTable.theadTr}>
            {allHeaders.map((col, colIdx) => (
              <th
                key={col.key}
                className={cn(
                  equipTable.th,
                  'cursor-pointer select-none hover:text-primary/80',
                  col.key === 'patientName' && 'min-w-[160px]',
                  colIdx === 0 && equipTable.stickyTh
                )}
                onClick={() => { if (colIdx < baseColumns.length) handleSort(col.key) }}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {colIdx < baseColumns.length && (
                    <ArrowUpDown className={cn('h-3 w-3', sortKey === col.key ? 'opacity-100' : 'opacity-30')} />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((patient, pIdx) => (
            <motion.tr
              key={patient.patientId}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.15 + Math.min(pIdx * 0.04, 0.8) }}
              className={cn(
                onPatientClick ? equipTable.trClickable : equipTable.tr,
                equipTable.trAlt(pIdx),
                'group'
              )}
              onClick={() => onPatientClick?.(patient.patientId)}
            >
              <td className={cn(equipTable.td, 'font-medium', pIdx % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd)}>
                {patient.patientName}
              </td>
              <td className={equipTable.td}>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                    patient.isMet === true
                      ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                      : patient.isMet === false
                        ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                        : 'bg-muted text-muted-foreground'
                  )}
                >
                  {patient.isMet === true ? 'Met' : patient.isMet === false ? 'Not Met' : '—'}
                </span>
              </td>
              {measureColumns.map((mc, i) => (
                <td key={i} className={equipTable.td}>{mc.accessor(patient)}</td>
              ))}
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════
   AUDIT DRILLDOWN — Coding / Quality_Audits
   ═══════════════════════════════════════════════════════════════════════ */

interface ClinicianWithAudits extends LeaderAuditClinician {
  audits?: AuditListItem[]
  auditsLoading?: boolean
}

function AuditDrilldown({
  measureName,
  leaderEmail,
  year,
  quarter,
  measure,
  onAuditClick,
}: ADCOEMeasureDrilldownProps) {
  const accent = getAccent(measure)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [expanded, setExpanded] = useState({})
  const [globalFilter, setGlobalFilter] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  // Derive auditType from measureName
  const auditType = measureName === 'Coding' ? 'coding' : 'quality'

  // Communicated (display) pass threshold for this audit measure, 0-100 scale,
  // sourced from och.equip_config_measure_schedule. Coding/Quality_Audits are
  // uniform across provider types, so a type-agnostic lookup is exact. Falls
  // back to 90 if the config row hasn't loaded.
  const thresholdMap = useMeasureThresholdMap(year, quarter)
  const displayThreshold = useMemo(() => {
    const item = thresholdMap.items.find(
      (i) => i.isActive && i.measureName === measureName
    )
    return item ? item.thresholdDisplay * 100 : 90
  }, [thresholdMap.items, measureName])

  // React Query: fetch clinician list
  const { data: clinicianData = [], isLoading, isError } = useLeaderAuditClinicians(
    leaderEmail,
    auditType,
    year,
    quarter
  )

  // Local enriched state for imperative audit loading
  const [clinicians, setClinicians] = useState<ClinicianWithAudits[]>([])

  useEffect(() => {
    setClinicians(clinicianData)
  }, [clinicianData])

  // Imperative load audits on first expand
  const loadClinicianAudits = useCallback(
    async (providerEmail: string) => {
      setClinicians((prev) =>
        prev.map((c) =>
          c.providerEmail === providerEmail ? { ...c, auditsLoading: true } : c
        )
      )

      try {
        const audits = await equipApi.dashboard.getProviderAudits(
          providerEmail,
          auditType,
          year,
          quarter
        )

        setClinicians((prev) =>
          prev.map((c) =>
            c.providerEmail === providerEmail
              ? { ...c, audits, auditsLoading: false }
              : c
          )
        )
      } catch (error) {
        console.error('Error loading clinician audits:', error)
        setClinicians((prev) =>
          prev.map((c) =>
            c.providerEmail === providerEmail ? { ...c, auditsLoading: false } : c
          )
        )
      }
    },
    [auditType, year, quarter]
  )

  const handleToggleExpanded = useCallback(
    (providerEmail: string) => {
      const clinician = clinicians.find((c) => c.providerEmail === providerEmail)
      if (clinician && !clinician.audits && !clinician.auditsLoading) {
        loadClinicianAudits(providerEmail)
      }
    },
    [clinicians, loadClinicianAudits]
  )

  // Columns
  const columnHelper = createColumnHelper<ClinicianWithAudits>()
  const columns = useMemo(
    () => [
      columnHelper.accessor('providerName', {
        header: ({ column }) => <SortableHeader label="Clinician" column={column} />,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <ChevronRight
              className={cn(
                'h-4 w-4 shrink-0 transition-transform text-muted-foreground',
                row.getIsExpanded() && 'rotate-90'
              )}
            />
            <span className="text-sm font-medium">{row.original.providerName}</span>
          </div>
        ),
      }),
      columnHelper.accessor('bonusEligibleType', {
        header: ({ column }) => <SortableHeader label="Type" column={column} />,
        cell: ({ row }) => (
          <p className="text-sm">{row.original.bonusEligibleType ?? '—'}</p>
        ),
      }),
      columnHelper.accessor('totalAudits', {
        header: ({ column }) => <SortableHeader label="Total" column={column} />,
        cell: ({ row }) => <p className="text-sm">{row.original.totalAudits}</p>,
      }),
      columnHelper.accessor('passingAudits', {
        header: ({ column }) => <SortableHeader label="Passing" column={column} />,
        cell: ({ row }) => <p className="text-sm">{row.original.passingAudits}</p>,
      }),
      columnHelper.accessor('passRate', {
        header: ({ column }) => <SortableHeader label="Rate" column={column} />,
        cell: ({ row }) => {
          const rate = row.original.passRate
          return (
            <p
              className={cn(
                'text-sm font-medium',
                rate != null && rate >= displayThreshold
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : rate != null
                    ? 'text-red-600 dark:text-red-400'
                    : ''
              )}
            >
              {rate != null ? `${rate.toFixed(1)}%` : '—'}
            </p>
          )
        },
      }),
    ],
    [columnHelper, displayThreshold]
  )

  const table = useReactTable({
    data: clinicians,
    columns,
    state: { sorting, globalFilter, expanded },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  })

  // ResizeObserver for --container-w
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    cleanupRef.current?.()
    const sync = () => el.style.setProperty('--container-w', `${el.clientWidth}px`)
    sync()
    const ro = new ResizeObserver(() => sync())
    ro.observe(el)
    cleanupRef.current = () => ro.disconnect()
    return () => {
      ro.disconnect()
      cleanupRef.current = null
    }
  }, [isLoading])

  if (isLoading) return <DrilldownLoading message="Loading clinician breakdown..." />
  if (isError) return <DrilldownError message="Failed to load clinician breakdown" />
  if (clinicians.length === 0) return <DrilldownEmpty message="No clinicians found" />

  // Build stat tiles for audit measures (sum from clinician data)
  const totalAudits = clinicians.reduce((sum, c) => sum + c.totalAudits, 0)
  const passingAudits = clinicians.reduce((sum, c) => sum + c.passingAudits, 0)
  const failingAudits = totalAudits - passingAudits
  const auditStatTiles = measure
    ? [
        { value: totalAudits, label: 'Total Audits', variant: 'slate' as const },
        { value: passingAudits, label: 'Passing', variant: 'emerald' as const },
        { value: failingAudits, label: 'Failing', variant: 'amber' as const },
      ]
    : []

  return (
    <div className="space-y-4">
      {/* Hero stats */}
      {measure && <HeroStats measure={measure} accent={accent} statTiles={auditStatTiles} />}

      {/* Measure info panel */}
      <MeasureInfoPanel measureName={measureName} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Clinician Breakdown
        </h4>
        <div className="flex items-center gap-2">
          {!showSearch ? (
            <Button variant="ghost" size="icon" onClick={() => setShowSearch(true)}>
              <Search className="h-4 w-4" />
            </Button>
          ) : (
            <Input
              placeholder="Search clinicians..."
              className="w-56"
              autoFocus
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              onBlur={() => {
                if (!globalFilter) setShowSearch(false)
              }}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <div className={equipTable.wrapper}>
        <div ref={scrollContainerRef} className="overflow-auto">
          <AnimatedTableWrapper>
            <table className={equipTable.table}>
              <thead className={equipTable.thead}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={equipTable.theadTr}>
                    {headerGroup.headers.map((header, headerIdx) => (
                      <th
                        key={header.id}
                        className={cn(equipTable.th, headerIdx === 0 && equipTable.stickyTh)}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <AnimatedTableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-4 text-muted-foreground"
                    >
                      No clinicians match your search
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, index) => (
                    <Fragment key={row.id}>
                      <AnimatedTableRow
                        index={index}
                        className={`${equipTable.tr} ${equipTable.trAlt(index)} cursor-pointer`}
                        onClick={() => {
                          row.toggleExpanded()
                          handleToggleExpanded(row.original.providerEmail)
                        }}
                      >
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td
                            key={cell.id}
                            className={cn(
                              equipTable.td,
                              cellIdx === 0 &&
                                (index % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd)
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </AnimatedTableRow>
                      <AnimatePresence initial={false}>
                        {row.getIsExpanded() && (
                          <tr>
                            <td colSpan={columns.length} className="p-0">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  height: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
                                  opacity: { duration: 0.25, delay: 0.08 },
                                }}
                                style={{ clipPath: 'inset(0)' }}
                              >
                                <div
                                  className="sticky left-0 [overflow:clip]"
                                  style={{ width: 'var(--container-w)' }}
                                >
                                  <SubGrid isExpanded={true} depth={1} variant="compact">
                                    <SubGridContent>
                                      {row.original.auditsLoading ? (
                                        <div className="flex items-center gap-2 py-4 px-3">
                                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                          <p className="text-sm text-muted-foreground">
                                            Loading audits...
                                          </p>
                                        </div>
                                      ) : row.original.audits &&
                                        row.original.audits.length > 0 ? (
                                        <AuditSubTable
                                          audits={row.original.audits}
                                          auditType={auditType}
                                          onAuditClick={onAuditClick}
                                        />
                                      ) : (
                                        <p className="text-sm text-muted-foreground py-3 px-3">
                                          No audit data available
                                        </p>
                                      )}
                                    </SubGridContent>
                                  </SubGrid>
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </Fragment>
                  ))
                )}
              </AnimatedTableBody>
            </table>
          </AnimatedTableWrapper>
        </div>
      </div>
    </div>
  )
}

/* ─── Audit Sub-Table ─── */

function AuditSubTable({
  audits,
  auditType,
  onAuditClick,
}: {
  audits: AuditListItem[]
  auditType: string
  onAuditClick?: (auditId: number, auditType: string) => void
}) {
  const { sorted, sortKey, handleSort } = useClientSort(audits, 'auditDate', 'desc')

  const auditColumns = [
    { key: 'auditDate', label: 'Date' },
    { key: 'score', label: 'Score' },
    { key: 'percentage', label: '%' },
    { key: 'auditor', label: 'Auditor' },
  ]

  return (
    <div className="border rounded-lg border-ld overflow-x-auto overscroll-x-contain bg-card">
      <table className={equipTable.table}>
        <thead className={equipTable.thead}>
          <tr className={equipTable.theadTr}>
            {auditColumns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  equipTable.th,
                  'cursor-pointer select-none hover:text-primary/80'
                )}
                onClick={() => handleSort(col.key)}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  <ArrowUpDown
                    className={cn(
                      'h-3 w-3',
                      sortKey === col.key ? 'opacity-100' : 'opacity-30'
                    )}
                  />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((audit, aIdx) => (
            <motion.tr
              key={audit.auditId}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.15 + Math.min(aIdx * 0.04, 0.8) }}
              className={cn(
                onAuditClick ? equipTable.trClickable : equipTable.tr,
                equipTable.trAlt(aIdx),
                'group'
              )}
              onClick={() => onAuditClick?.(audit.auditId, auditType)}
            >
              <td className={equipTable.td}>
                {audit.auditDate
                  ? new Date(audit.auditDate).toLocaleDateString()
                  : '—'}
              </td>
              <td className={equipTable.td}>
                {audit.score != null && audit.maxScore != null
                  ? `${audit.score}/${audit.maxScore}`
                  : '—'}
              </td>
              <td className={equipTable.td}>
                {audit.percentage != null ? (
                  <span
                    className={cn(
                      'font-medium',
                      audit.percentage >= 90
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-red-600 dark:text-red-400'
                    )}
                  >
                    {audit.percentage.toFixed(1)}%
                  </span>
                ) : (
                  '—'
                )}
              </td>
              <td className={equipTable.td}>{audit.auditor ?? '—'}</td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
